import {
  Box,
  Button,
  List,
  ListItemIcon,
  styled,
  Typography
} from '@mui/material';
import { Icon } from '@sdb/planning-ui';
import { toPlanningTasksWithDropzones } from 'components/roster-portal/activity-planning/utils';
import {
  NEW_GREEN_LIGHT,
  NEW_RED_LIGHT,
  ORANGE_LIGHT,
  rgbaPrimary
} from 'constants/colors';
import { useSelector } from 'hooks';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CategoryBadgeWithTooltip } from './components/CategoryBadgeWithTooltip';
import { StyledTaskListHeader } from './components/TaskList.styles';
import TaskListActionBar from './components/TaskListActionBar';
import TaskListWrapper from './components/TaskListWrapper';

import type { PlanningEntity } from 'components/roster-portal/activity-planning/types';
import type { EventResponse } from 'generated/index';
import type { PlanningTask } from './taskListData.types';
import type { RouteEntity, ScheduleTask, TaskListProps } from './types';
import { TaskListCategory } from './types';

export const TASK_LIST_WIDTH = 320;

const IconButton = styled(Button)`
  position: fixed;
  right: 0px;
  min-width: inherit;
  width: 40px;
  height: 40px;
  border-radius: 50%;

  > * {
    min-width: 0;
    svg {
      min-width: inherit;
    }
  }
`;

// NOTE: DropzoneComponent and SortedToPlanListComponent are required props with no defaults.
// This prevents @dnd-kit from being statically imported into the Pragmatic view bundle.
// CardsViewPragmatic passes Pragmatic equivalents; all other views pass the original dnd-kit versions.
// See CardsViewPragmatic/README.md for the full isolation strategy and dnd-kit removal steps.

const TaskList = ({
  onOpen,
  onClose,
  planningEntities,
  dropzonePadding,
  activeEntityContainingSelection,
  onChangeActiveEntity,
  onChangeSelection,
  onChangeMousePosition,
  onRouteClick,
  isTimeline = false,
  DropzoneComponent,
  SortedToPlanListComponent
}: TaskListProps) => {
  const planningEntitiesArr: PlanningEntity[] = Object.values(
    planningEntities || {}
  );

  // Get all planning entities of type 'ORGANIZATIONAL_UNIT_TO_PLAN'
  const planningEntitiesToPlan: PlanningEntity[] = planningEntitiesArr.filter(
    ({ type }) => type === 'ORGANIZATIONAL_UNIT_TO_PLAN'
  );

  const planEventsPerEntity = planningEntitiesToPlan.map((planningEntity) => {
    const planEvents =
      toPlanningTasksWithDropzones(
        (planningEntity?.planningTasks as PlanningTask[]) ?? []
      )
        ?.filter((task) => task.category === 'APPOINTMENT_TO_PLAN')
        .flatMap((task) => task.data || [])
        .filter(
          (event): event is EventResponse =>
            event !== undefined &&
            'category' in event &&
            event.category !== 'DROPZONE'
        ) ?? [];

    return {
      planningEntityId: planningEntity.planningEntityIdentifier.entityId,
      planEvents,
      planningEntity
    };
  });

  const routeEntities: RouteEntity[] = Object.values(
    planningEntities || {}
  ).filter((entity) => entity.type === 'ROUTE') as RouteEntity[];

  const scheduleToAssign: ScheduleTask[] =
    routeEntities.length > 0
      ? routeEntities.flatMap(
          (routeEntity) =>
            routeEntity.planningTasks
              ?.filter(
                (planningTask) =>
                  planningTask.category === 'SCHEDULE_TO_ASSIGN' &&
                  planningTask?.data
              )
              .map((planningTask) => ({
                data: planningTask.data
              }))
        )
      : [];

  const scheduleWithMultipleDuties: ScheduleTask[] =
    routeEntities.length > 0
      ? routeEntities.flatMap(
          (routeEntity) =>
            routeEntity.planningTasks
              ?.filter(
                (planningTask) =>
                  planningTask.category === 'SCHEDULE_WITH_MULTIPLE_DUTIES' &&
                  planningTask?.data
              )
              .map((planningTask) => ({ data: planningTask.data }))
        )
      : [];
  const amountOfScheduleWithMultipleDuties =
    scheduleWithMultipleDuties?.length ?? 0;

  const { hasActivityPlanningEditAccess } = useSelector(({ grants }) => grants);

  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [uncollapsedCategories, setOpenCategories] = useState<
    TaskListCategory[]
  >([]);

  const categories = Object.values(TaskListCategory).filter(
    (c): c is TaskListCategory => typeof c === 'string'
  ) as TaskListCategory[];
  const allCategoriesAreUncollapsed =
    uncollapsedCategories.length === categories.length;

  const handleClick = (category: TaskListCategory) => {
    setOpenCategories(
      uncollapsedCategories.includes(category)
        ? uncollapsedCategories.filter((c) => c !== category)
        : [...uncollapsedCategories, category]
    );
  };

  const handleSingleOpenCategory = (category: TaskListCategory) => {
    if (uncollapsedCategories.length > 1) return setOpenCategories([category]);

    setOpenCategories((prevCategories) => {
      if (prevCategories.includes(category)) return [];
      return [category];
    });
  };

  const collapseAllCategories = () => {
    setOpenCategories(allCategoriesAreUncollapsed ? [] : categories);
  };

  const handleDrop = () => {
    setOpenCategories((prevCategories) => {
      if (!prevCategories.includes(TaskListCategory.TO_PLAN_APPOINTMENTS)) {
        return [...prevCategories, TaskListCategory.TO_PLAN_APPOINTMENTS];
      }
      return prevCategories;
    });
  };

  useEffect(() => {
    isOpen ? onOpen?.() : onClose?.();
  }, [isOpen]);

  const getCategoryColor = (category: TaskListCategory): string => {
    const categoryString = TaskListCategory[TaskListCategory[category]];

    switch (categoryString) {
      case TaskListCategory.TO_PLAN_APPOINTMENTS:
        return NEW_GREEN_LIGHT;
      case TaskListCategory.SCHEDULE_TO_ASSIGN:
        return ORANGE_LIGHT;
      case TaskListCategory.SCHEDULE_WITH_MULTIPLE_DUTIES:
        return NEW_RED_LIGHT;
      default:
        return 'transparent';
    }
  };

  const amountOfPlanningEntities = planEventsPerEntity.reduce(
    (acc, { planEvents }) =>
      acc +
      planEvents.filter((item) => (item?.category as string) !== 'DROPZONE')
        .length,
    0
  );

  const amountOfScheduleToAssign = scheduleToAssign?.length;
  const totalTaskListItems =
    (amountOfPlanningEntities || 0) +
    (amountOfScheduleToAssign || 0) +
    (amountOfScheduleWithMultipleDuties || 0);

  const amountOfToPlan = planEventsPerEntity.reduce(
    (acc, { planEvents }) => acc + planEvents.length,
    0
  );

  return (
    <DropzoneComponent
      dropzonePadding={dropzonePadding}
      handleDrop={handleDrop}
    >
      <List
        aria-labelledby="nested-list"
        subheader={
          <StyledTaskListHeader>
            <Box
              sx={{
                display: 'flex',
                gap: '8px'
              }}
            >
              <Typography
                variant="h6"
                component="h3"
                lineHeight="28px"
                sx={{ fontSize: '18px' }}
              >
                {t('taskList.title')} ({totalTaskListItems})
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {amountOfToPlan > 0 && (
                  <CategoryBadgeWithTooltip
                    category={TaskListCategory.TO_PLAN_APPOINTMENTS}
                    count={amountOfToPlan ?? 0}
                    getCategoryColor={getCategoryColor}
                    handleSingleOpenCategory={() =>
                      handleSingleOpenCategory(
                        TaskListCategory.TO_PLAN_APPOINTMENTS
                      )
                    }
                    label={t(
                      `taskList.categories.${TaskListCategory.TO_PLAN_APPOINTMENTS}`
                    )}
                  />
                )}
                {amountOfScheduleToAssign > 0 && (
                  <CategoryBadgeWithTooltip
                    category={TaskListCategory.SCHEDULE_TO_ASSIGN}
                    count={amountOfScheduleToAssign ?? 0}
                    getCategoryColor={getCategoryColor}
                    handleSingleOpenCategory={() =>
                      handleSingleOpenCategory(
                        TaskListCategory.SCHEDULE_TO_ASSIGN
                      )
                    }
                    label={t(
                      `taskList.categories.${TaskListCategory.SCHEDULE_TO_ASSIGN}`
                    )}
                  />
                )}
                {amountOfScheduleWithMultipleDuties > 0 && (
                  <CategoryBadgeWithTooltip
                    category={TaskListCategory.SCHEDULE_WITH_MULTIPLE_DUTIES}
                    count={amountOfScheduleWithMultipleDuties ?? 0}
                    getCategoryColor={getCategoryColor}
                    handleSingleOpenCategory={() =>
                      handleSingleOpenCategory(
                        TaskListCategory.SCHEDULE_WITH_MULTIPLE_DUTIES
                      )
                    }
                    label={t(
                      `taskList.categories.${TaskListCategory.SCHEDULE_WITH_MULTIPLE_DUTIES}`
                    )}
                  />
                )}
              </Box>
            </Box>
            <IconButton
              onClick={() => setIsOpen(!isOpen)}
              variant="text"
              sx={{
                right: '16px',
                bgcolor: isOpen ? 'inherit' : rgbaPrimary(0.1),
                border: 0
              }}
            >
              <ListItemIcon>
                <Icon
                  type={isOpen ? 'angles-right' : 'angles-left'}
                  fontSize="small"
                />
              </ListItemIcon>
            </IconButton>
          </StyledTaskListHeader>
        }
        sx={{
          position: 'relative',
          zIndex: 40,
          width: TASK_LIST_WIDTH,
          right: isOpen ? 0 : `-${TASK_LIST_WIDTH}px`,
          transitionDuration: '250ms',
          backgroundColor: 'transparent',
          padding: '16px'
        }}
      >
        <TaskListActionBar
          collapseAllCategories={collapseAllCategories}
          allCategoriesAreUncollapsed={allCategoriesAreUncollapsed}
        />

        <TaskListWrapper
          categories={categories}
          uncollapsedCategories={uncollapsedCategories}
          planEventsPerEntity={planEventsPerEntity}
          scheduleToAssign={scheduleToAssign}
          scheduleWithMultipleDuties={scheduleWithMultipleDuties}
          getCategoryColor={getCategoryColor}
          handleClick={handleClick}
          activeEntityContainingSelection={activeEntityContainingSelection}
          onChangeActiveEntity={onChangeActiveEntity}
          onChangeSelection={onChangeSelection}
          hasActivityPlanningEditAccess={hasActivityPlanningEditAccess}
          onChangeMousePosition={onChangeMousePosition}
          onRouteClick={onRouteClick}
          isTimeline={isTimeline}
          amountOfToPlan={amountOfToPlan}
          SortedToPlanListComponent={SortedToPlanListComponent}
        />
      </List>
    </DropzoneComponent>
  );
};

export default TaskList;
